package info.rezident.app.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import info.rezident.app.core.AppErrorMessages
import info.rezident.app.core.AppTracking
import info.rezident.app.core.Logger
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.data.quiz.RecommendationItem
import info.rezident.app.pages.recommendations.*
import info.rezident.app.seo.setMetaNameTag
import info.rezident.app.ui.components.animations.ConfettiAnimation
import info.rezident.app.ui.components.sections.NavHeader
import info.rezident.app.ui.components.widgets.*
import info.rezident.app.ui.theme.AppTheme
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Progress

@Page("/recomandari")
@Composable
fun RecommendationsPage() {
    Header { }

    AutoScrollHandler()

    LaunchedEffect(Unit) {
        document.title = "💊 Recomandările Tale - rezident.info"
        document.setMetaNameTag(name = "robots", content = "noindex, nofollow")
    }

    val pageContext = rememberPageContext()
    val breakpoint = rememberBreakpoint()
    val viewModel = remember { RecommendationsViewModel() }
    val state by viewModel.state.collectAsState()

    var showConfetti by remember { mutableStateOf(false) }

    // Extract quizId from PageContext query parameters
    val quizId = pageContext.route.params["quizId"]

    // Handle null quizId with proper redirects
    LaunchedEffect(quizId) {
        if (quizId.isNullOrBlank()) {
            // Redirect if quizId is null
            val firebaseUser = AuthRepository.getCurrentUser()
            if (firebaseUser != null && !firebaseUser.isAnonymous) {
                pageContext.router.navigateTo("/profil")
            } else {
                pageContext.router.navigateTo("/quiz")
            }
        } else {
            // Initialize ViewModel only if quizId is provided
            viewModel.initializeWithQuizId(quizId)
        }
    }

    // Don't render if quizId is null (will redirect)
    if (quizId.isNullOrBlank()) {
        return
    }

    // Handle effects
    LaunchedEffect(Unit) {
        viewModel.effects.collectLatest { effect ->
            when (effect) {
                is RecommendationsEffect.NavigateTo -> {
                    window.location.assign(effect.url)
                }

                is RecommendationsEffect.ScrollToHash -> {
                    val element = document.getElementById(effect.hash)
                    element?.scrollIntoView(js("{ behavior: 'smooth', block: 'start' }"))
                }

                is RecommendationsEffect.ShowError -> {
                    // Handle error effect if needed
                }

                is RecommendationsEffect.ShowConfetti -> {
                    showConfetti = true
                }
            }
        }
    }

    LaunchedEffect(showConfetti) {
        if (showConfetti) {
            delay(6000)
            showConfetti = false
        }
    }

    Column(Modifier.fillMaxSize(), horizontalAlignment = Alignment.CenterHorizontally) {
        // Main content container
        NavHeaderSpacer()

        NavHeader(isSimple = true)

        // Gradient header per design
        GradientHeader(
            title = if (state.isUnlocked) {
                "Analiză Detaliată"
            } else {
                "Recomandările Tale"
            },
            subtitle = "Top 10".takeIf { state.recommendationsSorted == true },
            description = "Recomandările sunt personalizate, bazate pe studii, pentru a maximiza Potrivirea Persoană-Mediu (P-E Fit), reducând divergențele în carieră și riscul de burnout, cu scopul împlinirii profesionale pe termen lung.",
            isUnlocked = state.isUnlocked && !state.isLoading,
            modifier = Modifier.margin(bottom = 24.px)
        )

        Column(
            modifier = Modifier.fillMaxSize().padding(
                leftRight = if (breakpoint < Breakpoint.MD) 30.px else 36.px, bottom = 32.px
            ).maxWidth(1200.px),
            verticalArrangement = Arrangement.Top,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            when {
                state.isLoading -> {
                    LoadingState()
                }

                state.pageError != null -> {
                    when (state.pageError) {
                        RecommendationsPageError.OldQuizVersion -> {
                            OldQuizVersionState {
                                pageContext.router.navigateTo("/quiz")
                            }
                        }

                        RecommendationsPageError.QuizNotFound -> {
                            QuizNotFoundState(
                                isAnonymousOrNotSignedIn = state.isAnonymousUser || state.isNotSignedIn,
                                onAction = {
                                    if (state.isAnonymousUser || state.isNotSignedIn) {
                                        pageContext.router.navigateTo("/conectare")
                                    } else {
                                        pageContext.router.navigateTo("/quiz")
                                    }
                                }
                            )
                        }

                        RecommendationsPageError.RecommendationsNotAvailable -> {
                            RecommendationsNotFoundState {
                                pageContext.router.navigateTo("/quiz")
                            }
                        }

                        null -> {} // Should never happen due to outer check
                    }
                }

                else -> {
                    if (breakpoint >= Breakpoint.MD) {
                        DesktopLayout(
                            recommendations = state.recommendations,
                            incompatibleRecommendations = state.incompatibleRecommendations,
                            isUnlocked = state.isUnlocked,
                            creating = state.isCreatingCheckout,
                            priceText = state.selectedPriceText,
                            initialPriceText = state.selectedPriceInitialText,
                            discountText = state.selectedPriceDiscountText,
                            isAnonymousUser = state.isAnonymousUser,
                            isNotSignedIn = state.isNotSignedIn,
                            showAnswersSection = state.hasAnswers,
                            feedback = state.feedback,
                            isGeneratingProsCons = state.isGeneratingProsCons,
                            recommendationOrder = state.recommendationOrder,
                            showRanking = state.recommendationsSorted != false,
                            lockedRecommendationRanks = state.lockedRecommendationRanks,
                            prosConsGenerationError = state.prosConsGenerationError,
                            paymentError = state.paymentError,
                            onUnlock = { viewModel.startCheckout() },
                            onMyProfileClick = {
                                AppTracking.logEvent(
                                    AppTracking.EVENT_RECOMMENDATIONS_ACCOUNT_CLICK,
                                    mapOf(
                                        AppTracking.PARAM_TEMPLATE_ID to 1,
                                        "is_anonymous" to (state.isAnonymousUser || state.isNotSignedIn)
                                    )
                                )
                                if (state.isAnonymousUser || state.isNotSignedIn) {
                                    viewModel.setShowAccountLinking(true)
                                } else {
                                    pageContext.router.navigateTo("/profil")
                                }
                            },
                            onViewAnswersClick = {
                                AppTracking.logEvent(
                                    AppTracking.EVENT_RECOMMENDATIONS_ANSWERS_CLICK,
                                    mapOf(AppTracking.PARAM_TEMPLATE_ID to 1)
                                )
                                pageContext.router.navigateTo("/raspunsuri?quizId=$quizId")
                            },
                            onFeedbackChange = { viewModel.updateFeedback(it) },
                            onRetryProsCons = { viewModel.retryProsConsGeneration() }
                        )
                    } else {
                        MobileLayout(
                            recommendations = state.recommendations,
                            incompatibleRecommendations = state.incompatibleRecommendations,
                            isUnlocked = state.isUnlocked,
                            creating = state.isCreatingCheckout,
                            priceText = state.selectedPriceText,
                            initialPriceText = state.selectedPriceInitialText,
                            discountText = state.selectedPriceDiscountText,
                            isAnonymousUser = state.isAnonymousUser,
                            isNotSignedIn = state.isNotSignedIn,
                            showAnswersSection = state.hasAnswers,
                            feedback = state.feedback,
                            recommendationOrder = state.recommendationOrder,
                            showRanking = state.recommendationsSorted != false,
                            lockedRecommendationRanks = state.lockedRecommendationRanks,
                            prosConsGenerationError = state.prosConsGenerationError,
                            paymentError = state.paymentError,
                            onUnlock = { viewModel.startCheckout() },
                            onMyProfileClick = {
                                AppTracking.logEvent(
                                    AppTracking.EVENT_RECOMMENDATIONS_ACCOUNT_CLICK,
                                    mapOf(
                                        AppTracking.PARAM_TEMPLATE_ID to 1,
                                        "is_anonymous" to (state.isAnonymousUser || state.isNotSignedIn)
                                    )
                                )
                                if (state.isAnonymousUser || state.isNotSignedIn) {
                                    viewModel.setShowAccountLinking(true)
                                } else {
                                    pageContext.router.navigateTo("/profil")
                                }
                            },
                            onViewAnswersClick = {
                                AppTracking.logEvent(
                                    AppTracking.EVENT_RECOMMENDATIONS_ANSWERS_CLICK,
                                    mapOf(AppTracking.PARAM_TEMPLATE_ID to 1)
                                )
                                pageContext.router.navigateTo("/raspunsuri?quizId=$quizId")
                            },
                            onFeedbackChange = { viewModel.updateFeedback(it) },
                            isGeneratingProsCons = state.isGeneratingProsCons,
                            onRetryProsCons = { viewModel.retryProsConsGeneration() }
                        )
                    }
                }
            } // Close main content container
        } // Close main column
    }

    // Account linking dialog
    if (state.showAccountLinking) {
        AccountLinkingDialog(
            onDismiss = { viewModel.setShowAccountLinking(false) },
            onSuccess = { viewModel.onAccountLinkingSuccess() })
    }

    // Confetti overlay (absolute positioned)
    if (showConfetti) {
        ConfettiAnimation()
    }
}

@Composable
private fun NavHeaderSpacer() {
    Spacer(
        navHeaderHeightStyle.toModifier().id("acasa")
    )
}


@Composable
private fun LoadingState() {
    Column(
        modifier = Modifier.fillMaxWidth().padding(48.px), horizontalAlignment = Alignment.CenterHorizontally
    ) {
        var titleVersion by remember { mutableStateOf(0) }
        var showSubtitle by remember { mutableStateOf(false) }

        // Timed from when loading starts, so the messages also show up when anything before the
        // generation (auth, Firestore reads) is slow
        LaunchedEffect(Unit) {
            delay(5000)
            titleVersion = 1

            delay(5000)
            showSubtitle = true

            delay(5000)
            titleVersion = 2

            delay(10000)
            titleVersion = 3
        }

        SpanText(
            text = when (titleVersion) {
                0 -> "Analizăm Răspunsurile Tale..."
                1 -> "Comparăm Profilul Tău cu Specialitățile..."
                2 -> "Evaluăm Personalitatea și Preferințele..."
                3 -> "Finalizăm Ultimele Detalii..."
                else -> "Analizăm Răspunsurile Tale..."
            },
            modifier = AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
                .textAlign(TextAlign.Center)
        )

        if (showSubtitle) {
            SpanText(
                text = "Recomandările vor fi gata în 20 de secunde",
                modifier = AppTheme.typography.body2.toModifier().color(AppTheme.colors.textTertiary).margin(top = 6.px)
                    .textAlign(TextAlign.Center)
            )
        }

        Spacer(Modifier.height(16.px))

        Progress(Modifier.color(AppTheme.colors.text).toAttrs()) {}
    }
}

@Composable
private fun QuizNotFoundState(
    isAnonymousOrNotSignedIn: Boolean, onAction: () -> Unit
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxWidth().padding(64.px)
    ) {
        SpanText(
            text = "🤔", modifier = Modifier.fontSize(48.px).margin(bottom = 16.px)
        )
        SpanText(
            text = AppErrorMessages.QUIZ_NOT_FOUND_TITLE,
            modifier = Modifier.color(AppTheme.colors.text).margin(bottom = 8.px).fontSize(24.px).fontWeight(600)
                .textAlign(TextAlign.Center)
        )
        SpanText(
            text = if (isAnonymousOrNotSignedIn) {
                AppErrorMessages.QUIZ_NOT_FOUND_ANONYMOUS_BODY
            } else {
                AppErrorMessages.QUIZ_NOT_FOUND_AUTHENTICATED_BODY
            },
            modifier = Modifier.color(AppTheme.colors.textSecondary).margin(bottom = 24.px).textAlign(TextAlign.Center)
        )
        Button(
            onClick = { onAction() },
            Modifier.background(AppTheme.colors.accent),
            colorPalette = AppTheme.colors.accentColorPalette
        ) {
            SpanText(
                text = if (isAnonymousOrNotSignedIn) {
                    AppErrorMessages.SIGN_IN_ACTION
                } else {
                    AppErrorMessages.START_NOW_ACTION
                }, modifier = AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.onAccent)
            )
        }
    }
}

@Composable
private fun RecommendationsNotFoundState(onRetry: () -> Unit) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxWidth().padding(64.px)
    ) {
        SpanText(
            text = "🚀", modifier = Modifier.fontSize(48.px).margin(bottom = 16.px)
        )
        SpanText(
            text = AppErrorMessages.QUIZ_NOT_AVAILABLE_MESSAGE,
            modifier = Modifier.color(AppTheme.colors.text).margin(bottom = 8.px).fontSize(24.px).fontWeight(600)
                .textAlign(TextAlign.Center)
        )
        Spacer(modifier = Modifier.height(8.px))
        Button(onClick = { onRetry() }) {
            SpanText(
                text = AppErrorMessages.TAKE_QUIZ_ACTION,
                modifier = AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.background)
            )
        }
    }
}

@Composable
private fun OldQuizVersionState(onTakeNewQuiz: () -> Unit) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxWidth().padding(64.px)
    ) {
        SpanText(
            text = "🎉", modifier = Modifier.fontSize(48.px).margin(bottom = 16.px)
        )
        SpanText(
            text = "Avem vești bune!",
            modifier = Modifier.color(AppTheme.colors.text).margin(bottom = 8.px).fontSize(24.px).fontWeight(600)
                .textAlign(TextAlign.Center)
        )
        SpanText(
            text = "Am lansat o nouă versiune a Quiz-ului de Orientare, cu Recomandări mult îmbunătățite. Dacă ai plătit vechiul Quiz, îți oferim un Quiz nou gratis, scrie-ne pe Insta.",
            modifier = Modifier.color(AppTheme.colors.textSecondary).margin(bottom = 24.px).textAlign(TextAlign.Center)
                .lineHeight(1.5)
        )
        Button(
            onClick = { onTakeNewQuiz() },
            Modifier.background(AppTheme.colors.accent),
            colorPalette = AppTheme.colors.accentColorPalette
        ) {
            SpanText(
                text = "Încearcă noul Quiz",
                modifier = AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.onAccent)
            )
        }
    }
}

@Composable
private fun DesktopLayout(
    recommendations: List<RecommendationItem>,
    incompatibleRecommendations: List<RecommendationItem>,
    isUnlocked: Boolean,
    creating: Boolean,
    priceText: String?,
    initialPriceText: String?,
    discountText: String?,
    isAnonymousUser: Boolean,
    isNotSignedIn: Boolean,
    showAnswersSection: Boolean,
    feedback: RecommendationsFeedback,
    recommendationOrder: List<Int>,
    showRanking: Boolean,
    lockedRecommendationRanks: List<Int>,
    paymentError: PaymentError?,
    onUnlock: () -> Unit,
    onMyProfileClick: () -> Unit,
    onViewAnswersClick: () -> Unit,
    onFeedbackChange: (RecommendationsFeedback) -> Unit,
    isGeneratingProsCons: Boolean,
    prosConsGenerationError: ProsConsGenerationError?,
    onRetryProsCons: () -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(32.px),
        verticalAlignment = Alignment.Top
    ) {
        // Left column: Recommendations
        Column(
            modifier = Modifier.fillMaxWidth(67.percent), horizontalAlignment = Alignment.Start
        ) {
            InformationalNote(
                modifier = Modifier.margin(bottom = 24.px)
            )
            RecommendationsList(
                recommendations = recommendations,
                isUnlocked = isUnlocked,
                recommendationOrder = recommendationOrder,
                showRanking = showRanking,
                lockedRecommendationRanks = lockedRecommendationRanks,
                onUnlockClick = onUnlock,
                onPaywallClick = onUnlock,
                priceText = priceText,
                prosConsGenerationError = prosConsGenerationError,
                onRetryProsCons = onRetryProsCons,
                isGeneratingProsCons = isGeneratingProsCons
            )

            // Incompatible recommendations section
            if (incompatibleRecommendations.isNotEmpty()) {
                IncompatibleRecommendationsSection(incompatibleRecommendations, isUnlocked, showRanking = showRanking)
            }
        }

        // Right column: CTA
        Column(
            modifier = Modifier.fillMaxWidth(33.percent), horizontalAlignment = Alignment.Start
        ) {
            UnlockRecommendationsSection(
                creating = creating,
                onUnlock = onUnlock,
                isCompleted = isUnlocked,
                priceText = priceText,
                initialPriceText = initialPriceText,
                discountText = discountText,
                paymentError = paymentError
            )

            Spacer(modifier = Modifier.height(24.px))

            if (showAnswersSection) {
                ViewYourAnswersSection(
                    onViewAnswersClick = onViewAnswersClick
                )

                Spacer(modifier = Modifier.height(24.px))
            }

            SaveYourResultsSection(isCompleted = !isAnonymousUser && !isNotSignedIn) {
                onMyProfileClick()
            }

            Spacer(modifier = Modifier.height(24.px))

            // Feedback section in left column for desktop
            FeedbackSection(
                feedback = feedback, onFeedbackChange = onFeedbackChange
            )
        }
    }
}

@Composable
private fun MobileLayout(
    recommendations: List<RecommendationItem>,
    incompatibleRecommendations: List<RecommendationItem>,
    isUnlocked: Boolean,
    creating: Boolean,
    priceText: String?,
    initialPriceText: String?,
    discountText: String?,
    isAnonymousUser: Boolean,
    isNotSignedIn: Boolean,
    showAnswersSection: Boolean,
    feedback: RecommendationsFeedback,
    recommendationOrder: List<Int>,
    showRanking: Boolean,
    lockedRecommendationRanks: List<Int>,
    isGeneratingProsCons: Boolean,
    prosConsGenerationError: ProsConsGenerationError?,
    paymentError: PaymentError?,
    onUnlock: () -> Unit,
    onMyProfileClick: () -> Unit,
    onViewAnswersClick: () -> Unit,
    onFeedbackChange: (RecommendationsFeedback) -> Unit,
    onRetryProsCons: () -> Unit
) {
    // State for single card expansion (mobile only)
    var expandedCardRank by remember { mutableStateOf<Int?>(null) }

    // State for bottom sheet paywall modal
    var showBottomSheet by remember { mutableStateOf(false) }

    // State for sticky bottom CTA visibility
    var isStripVisible by remember { mutableStateOf(false) }
    var isScrolledPastHero by remember { mutableStateOf(false) }
    var showStickyBar by remember { mutableStateOf(false) }
    var debounceJob by remember { mutableStateOf<Job?>(null) }
    var expandedCardVisibility by remember { mutableStateOf(1.0) } // 0.0 = not visible, 1.0 = fully visible

    // Intersection observer for compact strip visibility
    DisposableEffect(Unit) {
        var stripObserver: dynamic = null
        val element = document.getElementById("compact-paywall-strip")
        if (element != null) {
            stripObserver =
                js("new IntersectionObserver(function(entries) { entries.forEach(function(entry) { if (entry.intersectionRatio >= 0.5) { window.stripVisible = true; } else { window.stripVisible = false; } }); }, { threshold: [0, 0.5, 1.0] })")
            stripObserver.observe(element)
        }

        onDispose {
            if (stripObserver != null) {
                stripObserver.disconnect()
            }
        }
    }

    // Intersection observer for expanded card visibility
    DisposableEffect(expandedCardRank) {
        var observer: dynamic = null

        if (expandedCardRank != null) {
            val cardElement = document.getElementById("recommendation-card-$expandedCardRank")
            if (cardElement != null) {
                observer =
                    js("new IntersectionObserver(function(entries) { entries.forEach(function(entry) { window.expandedCardVisibility = entry.intersectionRatio; }); }, { threshold: [0, 0.2, 0.5, 1.0] })")
                observer.observe(cardElement)
            }
        } else {
            // Reset visibility when no card is expanded
            expandedCardVisibility = 1.0
            js("window.expandedCardVisibility = 1.0")
        }

        // Cleanup: disconnect observer when effect re-runs or component unmounts
        onDispose {
            if (observer != null) {
                observer.disconnect()
            }
        }
    }

    // Scroll listener for hero visibility and expanded card tracking
    DisposableEffect(Unit) {
        val handleScroll: (dynamic) -> Unit = {
            val scrollY = window.scrollY
            isScrolledPastHero = scrollY > 350 // Adjust threshold as needed

            // Read strip visibility from global flag set by IntersectionObserver
            val stripVisibleFlag = js("window.stripVisible") as? Boolean ?: false
            isStripVisible = stripVisibleFlag

            // Read expanded card visibility from global flag
            val cardVisibility = js("window.expandedCardVisibility") as? Double ?: 1.0
            expandedCardVisibility = cardVisibility
        }

        window.addEventListener("scroll", handleScroll)

        onDispose {
            window.removeEventListener("scroll", handleScroll)
        }
    }

    // Debounced visibility logic
    LaunchedEffect(isStripVisible, showBottomSheet, isScrolledPastHero, expandedCardRank, expandedCardVisibility) {
        // Cancel previous debounce job
        debounceJob?.cancel()

        debounceJob = launch {
            delay(250) // 250ms debounce

            // Show sticky bar when...
            val shouldShow =
                !isUnlocked && isScrolledPastHero && !isStripVisible && !showBottomSheet && (expandedCardRank == null || expandedCardVisibility < 0.5)

            Logger.log("[StickyBar] isUnlocked=$isUnlocked, isScrolledPastHero=$isScrolledPastHero, isStripVisible=$isStripVisible, showBottomSheet=$showBottomSheet, expandedCardRank=$expandedCardRank, expandedCardVisibility=$expandedCardVisibility, shouldShow=$shouldShow")

            showStickyBar = shouldShow
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Yellow informational note under header
        InformationalNote(
            modifier = Modifier.margin(bottom = 24.px)
        )

        RecommendationsList(
            recommendations = recommendations,
            isUnlocked = isUnlocked,
            recommendationOrder = recommendationOrder,
            showRanking = showRanking,
            lockedRecommendationRanks = lockedRecommendationRanks,
            expandedCardRank = expandedCardRank,
            onExpandRequest = { rank -> expandedCardRank = if (expandedCardRank == rank) null else rank },
            onUnlockClick = onUnlock,
            onPaywallClick = { showBottomSheet = true },
            priceText = priceText,
            isGeneratingProsCons = isGeneratingProsCons,
            prosConsGenerationError = prosConsGenerationError,
            onRetryProsCons = onRetryProsCons,
        )

        // Incompatible recommendations section
        if (incompatibleRecommendations.isNotEmpty()) {
            IncompatibleRecommendationsSection(
                incompatibleRecommendations = incompatibleRecommendations,
                isUnlocked = isUnlocked,
                showRanking = showRanking,
                onPaywallClick = { showBottomSheet = true }
            )
        }

        if (showAnswersSection) {
            ViewYourAnswersSection(
                onViewAnswersClick = onViewAnswersClick
            )

            Spacer(modifier = Modifier.height(24.px))
        }

        SaveYourResultsSection(isCompleted = !isAnonymousUser && !isNotSignedIn) {
            onMyProfileClick()
        }

        Spacer(modifier = Modifier.height(24.px))

        // Feedback section at bottom for mobile
        FeedbackSection(
            feedback = feedback, onFeedbackChange = onFeedbackChange
        )

        // Spacer for sticky bottom bar
        Spacer(modifier = Modifier.height(80.px))
    }

    // Bottom sheet for unlock recommendations section
    BottomSheet(
        isVisible = showBottomSheet, onDismiss = { showBottomSheet = false }) {
        UnlockRecommendationsSection(
            creating = creating,
            isCompleted = isUnlocked,
            onUnlock = onUnlock,
            priceText = priceText,
            initialPriceText = initialPriceText,
            discountText = discountText,
            paymentError = paymentError,
            source = "bottom_sheet"
        )
    }

    // Sticky bottom CTA (mobile only, when quiz is locked)
    if (!isUnlocked) {
        StickyBottomCTA(
            isVisible = showStickyBar, priceText = priceText, onClick = { showBottomSheet = true })
    }
}

@Composable
private fun RecommendationsList(
    recommendations: List<RecommendationItem>,
    isUnlocked: Boolean,
    recommendationOrder: List<Int>,
    showRanking: Boolean,
    lockedRecommendationRanks: List<Int>,
    expandedCardRank: Int? = null,
    onExpandRequest: ((Int) -> Unit)? = null,
    onUnlockClick: () -> Unit,
    onPaywallClick: (() -> Unit)? = null,
    priceText: String? = null,
    isGeneratingProsCons: Boolean = false,
    prosConsGenerationError: ProsConsGenerationError? = null,
    onRetryProsCons: (() -> Unit)? = null,
) {
    recommendations.sortedBy { recommendationOrder.indexOf(it.rank) }.forEach { recommendation ->
        // Determine if this card should be locked:
        // - Quiz is locked AND recommendation rank is in the lockedRecommendationRanks list
        val isLocked = !isUnlocked && lockedRecommendationRanks.contains(recommendation.rank)
        EnhancedRecommendationCard(
            recommendation = recommendation,
            isLocked = isLocked,
            isQuizUnlocked = isUnlocked,
            showRanking = showRanking,
            expandedCardRank = expandedCardRank,
            onExpandRequest = onExpandRequest,
            onUnlockClick = onUnlockClick,
            onPaywallClick = onPaywallClick,
            priceText = priceText,
            aiError = prosConsGenerationError,
            onRetryAi = onRetryProsCons,
            isGeneratingProsCons = isGeneratingProsCons,
            modifier = Modifier.margin(bottom = 24.px)
        )
    }
}

@Composable
private fun IncompatibleRecommendationsSection(
    incompatibleRecommendations: List<RecommendationItem>,
    isUnlocked: Boolean,
    showRanking: Boolean,
    onPaywallClick: (() -> Unit)? = null
) {
    Column(
        modifier = Modifier.fillMaxWidth().margin(top = 24.px)
    ) {
        // Section header
        SpanText(
            text = "Specialități Incompatibile",
            modifier = AppTheme.typography.h2.toModifier().color(AppTheme.colors.text).margin(bottom = 8.px)
        )
        SpanText(
            text = "Aceste specialități au obținut un Scor de Potrivire scăzut în raport cu profilul tău, dupa analiza Persoană-Mediu (P-E Fit), dar și a aptitudinilor și dorințelor tale.",
            modifier = AppTheme.typography.body1.toModifier().color(AppTheme.colors.textSecondary)
                .margin(bottom = 24.px).lineHeight(1.5)
        )

        // Display incompatible recommendation cards
        // Last 3 should be shown as locked when quiz is locked
        incompatibleRecommendations.forEachIndexed { index, recommendation ->
            // Lock the last 3 recommendations when quiz is locked
            val shouldBeLocked = !isUnlocked && index >= incompatibleRecommendations.size - 3

            if (shouldBeLocked) {
                // Show as a locked card (grayed out, non-expandable)
                LockedRecommendationCard(
                    recommendation = recommendation,
                    isWorstCard = true,
                    showRanking = showRanking,
                    onClick = onPaywallClick,
                    modifier = Modifier.margin(bottom = 24.px)
                )
            } else {
                // Show as a normal incompatible card
                RecommendationCardContent(
                    recommendation = recommendation,
                    isQuizUnlocked = isUnlocked,
                    isWorstCard = true,
                    modifier = Modifier.margin(bottom = 24.px)
                )
            }
        }
    }
}

@Composable
private fun AutoScrollHandler() {
    val hash = window.location.hash
    LaunchedEffect(hash) {
        if (hash.isNotEmpty()) {
            val element = document.getElementById(hash.substring(1))
            element?.scrollIntoView(js("{ behavior: 'smooth', block: 'start' }"))
        }
    }
}